import { useState, useEffect } from 'react';
import {
  Menu,
  Button,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { UserModel } from 'models';
import BasicUserCard from '../BasicUserCard/BasicUserCard';

export interface AdminUserDropdownProps {
  users: UserModel[];
  onUserSelect?: (users: UserModel[]) => void;
  multiSelectable?: boolean;
  defaultSelectedUsers?: UserModel[];
  maxWidth?: boolean;
}

export default function AdminUserDropdown({
  users,
  onUserSelect,
  defaultSelectedUsers = [],
  multiSelectable = false,
  maxWidth = false
}: AdminUserDropdownProps) {
  const [selectedUsers, setSelectedUsers] = useState<UserModel[]>(defaultSelectedUsers);
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const highlightBorder = useColorModeValue('blue.500', 'blue.300');

  useEffect(() => {
    setSelectedUsers(defaultSelectedUsers);
  }, [defaultSelectedUsers]);

  const handleUserClick = (user: UserModel) => {
    let newSelectedUsers: UserModel[];

    if (multiSelectable) {
      const isSelected = selectedUsers.some(u => u.id === user.id);
      if (isSelected) {
        newSelectedUsers = selectedUsers.filter(u => u.id !== user.id);
      } else {
        newSelectedUsers = [...selectedUsers, user];
      }
    } else {
      newSelectedUsers = [user];
    }

    setSelectedUsers(newSelectedUsers);
    onUserSelect?.(newSelectedUsers);
  };

  const isUserSelected = (userId: string): boolean => {
    return selectedUsers.some(u => u.id === userId);
  };

  const menuContent = users.map((user, index) => {
    const isSelected = isUserSelected(user.id);
    return (
      <MenuItem
        key={`adminUserDropdown_user_${index}`}
        padding={'0px'}
        px={2}
        onClick={() => handleUserClick(user)}
        backgroundColor={isSelected ? highlightBg : 'transparent'}
        borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
        borderColor={isSelected ? highlightBorder : 'transparent'}
        _hover={{ backgroundColor: isSelected ? highlightBg : 'gray.100' }}
      >
        <BasicUserCard
          avatarSize='sm'
          titleSize='sm'
          user={user}
          showBorder={false}
          showEmail={true}
        />
      </MenuItem>
    );
  });

  function renderMenuButton() {
    if (multiSelectable) {
      if (selectedUsers.length > 0) {
        return (
          <Text fontSize="sm" color="gray.300">
            {`${selectedUsers.length} Users Selected`}
          </Text>
        );
      } else {
        return <Text fontSize="sm" color="gray.300">Select Users</Text>;
      }
    } else if (selectedUsers.length > 0) {
      return (
        <BasicUserCard
          avatarSize='sm'
          titleSize='sm'
          user={selectedUsers[0]}
          showBorder={false}
          showEmail={false}
        />
      );
    } else {
      return <Text fontSize="sm" color="gray.300">Select A User</Text>;
    }
  }

  return (
    <Menu closeOnSelect={!multiSelectable}>
      <MenuButton
        borderRadius='8px'
        variant='outline'
        textAlign='left'
        width={maxWidth ? "100%" : "auto"}
        as={Button}
        rightIcon={<ChevronDownIcon />}
      >
        {renderMenuButton()}
      </MenuButton>
      <MenuList width="100%" maxHeight="300px" overflow="scroll" backgroundColor={'white'} zIndex='100'>
        {menuContent}
      </MenuList>
    </Menu>
  );
}
